'use client'

import { useEffect, useRef } from 'react'
import './HeroImage.css'

interface HeroImageProps {
  backgroundImage: string
  title: string
  subtitle?: string
  location?: string
  showCTA?: boolean
  ctaText?: string
  ctaHref?: string
}

export default function HeroImage({
  backgroundImage,
  title,
  subtitle,
  location,
  showCTA = false,
  ctaText = 'Más información',
  ctaHref = '#details',
}: HeroImageProps) {
  const imgRef = useRef<HTMLImageElement>(null)
  const bgRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Show previous hero as instant placeholder while new one loads
    try {
      const prev = sessionStorage.getItem('apex_last_hero')
      if (prev && (prev.startsWith('/images/') || prev.startsWith('https://')) && bgRef.current) {
        bgRef.current.style.backgroundImage = `url("${prev}")`
        bgRef.current.style.backgroundSize = 'cover'
        bgRef.current.style.backgroundPosition = 'center'
      }
    } catch {}

    const el = imgRef.current
    if (!el) return
    // Image may already be loaded from cache before onLoad handler was attached
    if (el.complete && el.naturalWidth > 0) {
      el.style.opacity = '1'
      try { sessionStorage.setItem('apex_last_hero', backgroundImage) } catch {}
    }
  }, [])

  return (
    <section className="hero-image" data-hero-background>
      {/* Background Image (z-index: -1) */}
      <div ref={bgRef} className="hero-background">
        <img
          ref={imgRef}
          src={backgroundImage}
          alt={title}
          className="hero-bg-image"
          loading="eager"
          fetchPriority="high"
          onLoad={(e) => {
            const img = e.currentTarget as HTMLImageElement
            requestAnimationFrame(() => { img.style.opacity = '1' })
            try { sessionStorage.setItem('apex_last_hero', backgroundImage) } catch {}
          }}
          style={{ opacity: 0, transition: 'opacity 0.5s ease' }}
        />
        <div className="hero-overlay"></div>
      </div>

      {/* Hero Content Superpuesto (z-index: 2) */}
      <div className="hero-content">
        {location && <p className="hero-location">{location}</p>}

        <h1 className="hero-title">{title}</h1>

        {subtitle && <p className="hero-subtitle">{subtitle}</p>}

        {showCTA && (
          <a href={ctaHref} className="hero-cta-btn">
            {ctaText} →
          </a>
        )}
      </div>
    </section>
  )
}
